import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Menu, 
  Clock, 
  Plus, 
  Edit, 
  Trash2, 
  Save,
  X
} from 'lucide-react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  getDocs,
  setDoc,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { toast } from 'react-hot-toast';
import { MenuCategory, MenuItem, OpeningHours, MenuExtra } from '../types';
import { uploadImage, generateImagePath, deleteImage, saveImageRecord, deleteImageRecordsByItemId, processMenuImage } from '../utils/imageUpload';
import { useOpeningHours } from '../contexts/OpeningHoursContext';
import ConfirmModal from '../components/ConfirmModal';
import { isCurrentlyOpen } from '../utils/checkOpenStatus';
import { CollapsibleCategory } from '../components/CollapsibleCategory';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const AdminDashboard: React.FC = () => {
  const { openingHours } = useOpeningHours();
  const [localOpeningHours, setLocalOpeningHours] = useState<OpeningHours>({
    monday: { open: '08:00', close: '17:00', closed: false },
    tuesday: { open: '08:00', close: '17:00', closed: false },
    wednesday: { open: '08:00', close: '17:00', closed: false },
    thursday: { open: '08:00', close: '17:00', closed: false },
    friday: { open: '08:00', close: '17:00', closed: false },
    saturday: { open: '08:00', close: '15:00', closed: false },
    sunday: { open: '08:00', close: '17:00', closed: true }
  });
  const [activeTab, setActiveTab] = useState<'menu' | 'hours'>('menu');
  const [onlineOrderingEnabled, setOnlineOrderingEnabled] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalType, setConfirmModalType] = useState<'enable' | 'disable'>('enable');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    type: 'category' | 'item' | null;
    id: string | null;
    name: string;
  }>({ show: false, type: null, id: null, name: '' });
  
  // Sync local state with context
  useEffect(() => {
    // Ensure all days have the closed property for backward compatibility
    const updatedHours: OpeningHours = {
      monday: { ...openingHours.monday, closed: openingHours.monday?.closed ?? false },
      tuesday: { ...openingHours.tuesday, closed: openingHours.tuesday?.closed ?? false },
      wednesday: { ...openingHours.wednesday, closed: openingHours.wednesday?.closed ?? false },
      thursday: { ...openingHours.thursday, closed: openingHours.thursday?.closed ?? false },
      friday: { ...openingHours.friday, closed: openingHours.friday?.closed ?? false },
      saturday: { ...openingHours.saturday, closed: openingHours.saturday?.closed ?? false },
      sunday: { ...openingHours.sunday, closed: openingHours.sunday?.closed ?? true }
    };
    setLocalOpeningHours(updatedHours);
    setHasHoursChanges(false); // Reset changes flag when syncing from server
  }, [openingHours]);

  // Check if opening hours have changed
  useEffect(() => {
    const hasChanges = JSON.stringify(localOpeningHours) !== JSON.stringify(openingHours);
    setHasHoursChanges(hasChanges);
  }, [localOpeningHours, openingHours]);

  // Auto-turn OFF live orders when shop transitions from open -> closed
  const prevIsOpenRef = useRef<boolean | null>(null);
  useEffect(() => {
    const nowOpen = isCurrentlyOpen(openingHours);
    const prevOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = nowOpen;
    if (prevOpen === true && nowOpen === false && onlineOrderingEnabled) {
      // Shop just closed while live orders were ON → switch OFF
      (async () => {
        try {
          await setDoc(doc(db, 'settings', 'ordering'), { enabled: false, updatedAt: new Date() });
          toast('Live orders turned OFF (shop is now closed)', { icon: '⏰', duration: 4000 });
        } catch (e) {
          console.error('Error turning off live orders on close:', e);
        }
      })();
    }
  }, [openingHours, onlineOrderingEnabled]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', order: 0, isActive: true });
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: 0,
    categoryId: '',
    imageUrl: '',
    order: 0,
    extras: [] as MenuExtra[],
    isAvailable: true
  });
  const [editingItemData, setEditingItemData] = useState({
    name: '',
    description: '',
    price: 0,
    categoryId: '',
    imageUrl: '',
    order: 0,
    extras: [] as MenuExtra[],
    isAvailable: true
  });
  const [editingCategoryData, setEditingCategoryData] = useState({
    name: '',
    description: '',
    order: 0,
    isActive: true
  });
  const [newExtra, setNewExtra] = useState({ name: '', price: 0 });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageQualityNote, setImageQualityNote] = useState<string | null>(null);
  const [showAddCategoryDropdown, setShowAddCategoryDropdown] = useState(false);
  const [hasHoursChanges, setHasHoursChanges] = useState(false);

  useEffect(() => {
    // Listen to categories
    const categoriesQuery = query(collection(db, 'menuCategories'), orderBy('order', 'asc'));
    const unsubscribeCategories = onSnapshot(categoriesQuery, (snapshot) => {
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MenuCategory[];
      setCategories(categoriesData);
    });

    // Listen to menu items
    const menuItemsQuery = query(collection(db, 'menuItems'), orderBy('order', 'asc'));
    const unsubscribeMenuItems = onSnapshot(menuItemsQuery, (snapshot) => {
      const menuItemsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MenuItem[];
      setMenuItems(menuItemsData);
    });

    // Listen to settings
    const unsubscribeSettings = onSnapshot(doc(db, 'settings', 'ordering'), (doc) => {
      if (doc.exists()) {
        setOnlineOrderingEnabled(doc.data().enabled ?? true);
      }
    });

    return () => {
      unsubscribeCategories();
      unsubscribeMenuItems();
      unsubscribeSettings();
    };
  }, []);

  // Handle modal opening/closing
  useEffect(() => {
    const isModalOpen = showCategoryForm || showItemForm || editingCategory || editingItem;
    
    if (isModalOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Scroll to top of modal
      window.scrollTo(0, 0);
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCategoryForm, showItemForm, editingCategory, editingItem]);

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      console.log('Adding category:', newCategory);
      const docRef = await addDoc(collection(db, 'menuCategories'), {
        ...newCategory,
        order: categories.length
      });
      console.log('Category added with ID:', docRef.id);
      setNewCategory({ name: '', description: '', order: 0, isActive: true });
      setShowCategoryForm(false);
      toast.success('Category added successfully');
    } catch (error) {
      console.error('Error adding category:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to add category: ${errorMessage}`);
    }
  };

  const handleUpdateCategory = async (categoryId: string, updates: Partial<MenuCategory>) => {
    try {
      await updateDoc(doc(db, 'menuCategories', categoryId), updates);
      toast.success('Category updated successfully');
    } catch (error) {
      console.error('Error updating category:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to update category: ${errorMessage}`);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (category) {
      setDeleteConfirmation({
        show: true,
        type: 'category',
        id: categoryId,
        name: category.name
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.id || !deleteConfirmation.type) return;

    try {
      if (deleteConfirmation.type === 'category') {
        await deleteDoc(doc(db, 'menuCategories', deleteConfirmation.id));
        toast.success('Category deleted successfully');
      } else if (deleteConfirmation.type === 'item') {
        // Find the menu item to get its image URL
        const itemToDelete = menuItems.find(item => item.id === deleteConfirmation.id);
        
        // Clean up image if exists (automatically deletes from both Storage and Firestore)
        if (itemToDelete?.imageUrl) {
          try {
            await deleteImage(itemToDelete.imageUrl);
            console.log('✅ Image deleted from Storage and Firestore');
          } catch (imageError) {
            console.error('Error cleaning up image:', imageError);
            // Continue with item deletion even if image cleanup fails
          }
        }
        
        // Also delete any orphaned image records by itemId
        await deleteImageRecordsByItemId(deleteConfirmation.id);
        
        // Delete the menu item
        await deleteDoc(doc(db, 'menuItems', deleteConfirmation.id));
        
        toast.success('Menu item deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to delete: ${errorMessage}`);
    } finally {
      setDeleteConfirmation({ show: false, type: null, id: null, name: '' });
    }
  };

  const handleAddItem = async () => {
    if (!newItem.name.trim() || !newItem.categoryId || newItem.price <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setUploadingImage(true);
      console.log('Adding menu item:', newItem);
      
      // First add the menu item to get an ID
      const docRef = await addDoc(collection(db, 'menuItems'), {
        ...newItem,
        extras: newItem.extras || [],
        order: menuItems.length
      });
      
      let imageUrl = newItem.imageUrl;
      
      // Upload image if selected
      if (selectedImage) {
        try {
          console.log('Uploading image for item:', docRef.id);
          imageUrl = await uploadItemImage(docRef.id, selectedImage);
          console.log('Image uploaded successfully, URL:', imageUrl);
          
          // Save image record to images collection
          const imageId = `img_${docRef.id}_${Date.now()}`;
          await saveImageRecord({
            id: imageId,
            name: selectedImage.name,
            url: imageUrl,
            uploadedBy: 'admin', // You can get this from auth context
            type: 'menu-item',
            size: selectedImage.size,
            itemId: docRef.id
          });
          console.log('Image record saved to images collection');
          
          // Update the menu item with the image URL
          await updateDoc(docRef, { imageUrl });
          console.log('Menu item updated with image URL');
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          const errorMessage = imageError instanceof Error ? imageError.message : 'Unknown error';
          toast.error(`Menu item added but image upload failed: ${errorMessage}`);
        }
      }
      
      console.log('Menu item added with ID:', docRef.id);
      setNewItem({
        name: '',
        description: '',
        price: 0,
        categoryId: '',
        imageUrl: '',
        order: 0,
        extras: [],
        isAvailable: true
      });
      setNewExtra({ name: '', price: 0 });
      clearImageSelection();
      setShowItemForm(false);
      toast.success('Menu item added successfully');
    } catch (error) {
      console.error('Error adding menu item:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to add menu item: ${errorMessage}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdateItem = async (itemId: string, updates: Partial<MenuItem>) => {
    try {
      await updateDoc(doc(db, 'menuItems', itemId), updates);
      toast.success('Menu item updated successfully');
    } catch (error) {
      console.error('Error updating menu item:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to update menu item: ${errorMessage}`);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    const item = menuItems.find(itm => itm.id === itemId);
    if (item) {
      setDeleteConfirmation({
        show: true,
        type: 'item',
        id: itemId,
        name: item.name
      });
    }
  };

  const handleSaveHours = async () => {
    if (!hasHoursChanges) {
      toast('No changes to save', { icon: 'ℹ️' });
      return;
    }

    try {
      const settingsRef = doc(db, 'settings', 'openingHours');
      
      // Use setDoc which creates the document if it doesn't exist or updates if it does
      await setDoc(settingsRef, {
        openingHours: localOpeningHours
      });
      
      setHasHoursChanges(false);
      toast.success('Opening hours updated successfully');
    } catch (error) {
      console.error('Error updating opening hours:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to update opening hours: ${errorMessage}`);
    }
  };


  const handleAddExtraToForm = () => {
    if (!newExtra.name.trim()) {
      toast.error('Extra name is required');
      return;
    }
    if (newExtra.price < 0) {
      toast.error('Price must be non-negative');
      return;
    }

    const extra: MenuExtra = {
      id: Date.now().toString(), // Simple ID for form
      name: newExtra.name.trim(),
      price: newExtra.price,
      isActive: true
    };

    setNewItem({ ...newItem, extras: [...newItem.extras, extra] });
    setNewExtra({ name: '', price: 0 });
    toast.success('Extra added successfully');
  };

  const handleRemoveExtraFromForm = (extraId: string) => {
    setNewItem({
      ...newItem,
      extras: newItem.extras.filter(extra => extra.id !== extraId)
    });
  };

  const handleAddExtraToEditingForm = () => {
    if (!newExtra.name.trim()) {
      toast.error('Extra name is required');
      return;
    }
    if (newExtra.price < 0) {
      toast.error('Price must be non-negative');
      return;
    }

    const extra: MenuExtra = {
      id: Date.now().toString(), // Simple ID for form
      name: newExtra.name.trim(),
      price: newExtra.price,
      isActive: true
    };

    setEditingItemData({ ...editingItemData, extras: [...editingItemData.extras, extra] });
    setNewExtra({ name: '', price: 0 });
  };

  const handleRemoveExtraFromEditingForm = (extraId: string) => {
    setEditingItemData({
      ...editingItemData,
      extras: editingItemData.extras.filter(extra => extra.id !== extraId)
    });
  };

  // Image upload functions
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSelectedImage(null);
      setImagePreview(null);
      setImageQualityNote(null);
      return;
    }

    // Basic file size cap (5MB)
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error('Image too large. Please choose a file under 5MB.');
      return;
    }

    setSelectedImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setImagePreview(dataUrl);

      // Quality check: examine dimensions and compression ratio
      try {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          const pixels = width * height;

          // Infer raw size for JPEG-like images: width * height * 3 bytes
          const estimatedRawBytes = pixels * 3;
          const ratio = estimatedRawBytes > 0 ? estimatedRawBytes / file.size : 0;

          const notes: string[] = [];
          if (width < 800) {
            notes.push('Low resolution (width < 800px). It may look soft.');
          }
          if (ratio > 30) {
            notes.push('Heavily compressed; may appear blocky. Consider a clearer photo.');
          }

          if (notes.length > 0) {
            const noteMsg = notes.join(' ');
            setImageQualityNote(noteMsg);
            toast((t) => (
              <div>
                <div className="font-semibold">Image quality warning</div>
                <div className="text-sm">{noteMsg}</div>
              </div>
            ), { icon: '⚠️' });
          } else {
            setImageQualityNote(null);
          }
        };
        img.src = dataUrl;
      } catch (err) {
        // Non-blocking; just skip hints
        setImageQualityNote(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const uploadItemImage = async (itemId: string, file: File): Promise<string> => {
    // Downscale/convert before upload
    const processed = await processMenuImage(file, 1000, 0.82);
    const imagePath = generateImagePath(itemId, processed.name);

    // Add timeout to prevent infinite loading
    const uploadPromise = uploadImage(processed, imagePath);
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Upload timeout after 30 seconds')), 30000);
    });

    return await Promise.race([uploadPromise, timeoutPromise]);
  };

  const clearImageSelection = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageQualityNote(null);
  };

  const handleToggleOnlineOrdering = () => {
    // Check if shop is currently open
    const shopIsOpen = isCurrentlyOpen(openingHours);

    // Show confirmation modal for both enabling and disabling
    if (onlineOrderingEnabled) {
      // If disabling, show delete warning modal
      setConfirmModalType('disable');
      setShowConfirmModal(true);
    } else {
      // If enabling, check if shop is open
      if (!shopIsOpen) {
        toast.error('Cannot enable online ordering: shop is currently closed (see Opening Hours).', {
          duration: 5000,
          icon: '⏰'
        });
        return;
      }
      // If shop is open, show availability check reminder
      setConfirmModalType('enable');
      setShowConfirmModal(true);
    }
  };

  const confirmToggleOrdering = async () => {
    setSavingSettings(true);
    setShowConfirmModal(false);
    
    try {
      if (onlineOrderingEnabled) {
        // Disabling online ordering - keep existing orders
        console.log('Disabling online ordering. Existing orders will remain visible.');
        
        await setDoc(doc(db, 'settings', 'ordering'), {
          enabled: false,
          updatedAt: new Date()
        });
        
        toast.success('Online ordering disabled. Existing orders remain visible until completed.');
      } else {
        // Enabling online ordering - delete all orders and start fresh
        console.log('Enabling online ordering and clearing all order history...');
        
        // Get all orders
        const ordersRef = collection(db, 'orders');
        const ordersSnapshot = await getDocs(ordersRef);
        
        // Delete each order
        const deletePromises = ordersSnapshot.docs.map(orderDoc => 
          deleteDoc(doc(db, 'orders', orderDoc.id))
        );
        
        await Promise.all(deletePromises);
        console.log(`Deleted ${deletePromises.length} orders from database`);
        
        // Update settings to enable
        await setDoc(doc(db, 'settings', 'ordering'), {
          enabled: true,
          updatedAt: new Date()
        });
        
        toast.success(`Online ordering enabled! Order history cleared. New orders will start from #1.`);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Handle category reordering
  const handleCategoryReorder = async (reorderedCategories: MenuCategory[]) => {
    try {
      // Update order for each category
      const updatePromises = reorderedCategories.map((category, index) =>
        updateDoc(doc(db, 'menuCategories', category.id), { order: index })
      );
      await Promise.all(updatePromises);
      toast.success('Category order updated');
    } catch (error) {
      console.error('Error reordering categories:', error);
      toast.error('Failed to update category order');
    }
  };

  // Handle menu item reordering within a category
  const handleMenuItemReorder = async (categoryId: string, reorderedItems: MenuItem[]) => {
    try {
      // Update order for each item within the category (0, 1, 2, ...)
      const updatePromises = reorderedItems.map((item, index) =>
        updateDoc(doc(db, 'menuItems', item.id), { order: index })
      );
      await Promise.all(updatePromises);
      toast.success('Item order updated');
    } catch (error) {
      console.error('Error reordering items:', error);
      toast.error('Failed to update item order');
    }
  };

  // Handle adding item to specific category
  const handleAddItemToCategory = (categoryId: string) => {
    setNewItem({
      name: '',
      description: '',
      price: 0,
      categoryId: categoryId,
      imageUrl: '',
      order: 0,
      extras: [],
      isAvailable: true
    });
    setNewExtra({ name: '', price: 0 });
    setShowItemForm(true);
    window.scrollTo(0, 0);
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle category drag end
  const handleCategoryDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id);
      const newIndex = categories.findIndex((cat) => cat.id === over.id);

      const newOrder = arrayMove(categories, oldIndex, newIndex);
      handleCategoryReorder(newOrder);
    }
  };

  const handleEditCategory = (category: MenuCategory) => {
    setEditingCategory(category);
    setEditingCategoryData({
      name: category.name,
      description: category.description || '',
      order: category.order,
      isActive: category.isActive
    });
    // Scroll to top when opening edit modal
    window.scrollTo(0, 0);
  };

  const handleEditItem = (item: MenuItem) => {
    setEditingItem(item);
    setEditingItemData({
      name: item.name,
      description: item.description,
      price: item.price,
      categoryId: item.categoryId,
      imageUrl: item.imageUrl || '',
      order: item.order,
      extras: item.extras || [],
      isAvailable: item.isAvailable
    });
    // Clear any previous image selection
    clearImageSelection();
    // Scroll to top when opening edit modal
    window.scrollTo(0, 0);
  };

  const handleUpdateCategorySubmit = async () => {
    if (!editingCategory || !editingCategoryData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      await updateDoc(doc(db, 'menuCategories', editingCategory.id), editingCategoryData);
      setEditingCategory(null);
      toast.success('Category updated successfully');
    } catch (error) {
      console.error('Error updating category:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to update category: ${errorMessage}`);
    }
  };

  const handleUpdateItemSubmit = async () => {
    if (!editingItem || !editingItemData.name.trim() || !editingItemData.categoryId || editingItemData.price <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setUploadingImage(true);
      
      let imageUrl = editingItemData.imageUrl;
      const oldImageUrl = editingItem.imageUrl; // Store the old image URL
      
      // Upload new image if selected
      if (selectedImage) {
        try {
          imageUrl = await uploadItemImage(editingItem.id, selectedImage);
          
          // Save image record to images collection
          const imageId = `img_${editingItem.id}_${Date.now()}`;
          await saveImageRecord({
            id: imageId,
            name: selectedImage.name,
            url: imageUrl,
            uploadedBy: 'admin',
            type: 'menu-item',
            size: selectedImage.size,
            itemId: editingItem.id
          });
          console.log('Image record saved to images collection');
          
          // Delete old image from Storage if it exists and is different from new one
          if (oldImageUrl && oldImageUrl !== imageUrl) {
            try {
              await deleteImage(oldImageUrl);
              console.log('Old image deleted from Storage');
              
              // Also delete the old image record from images collection
              const imagesQuery = query(collection(db, 'images'), where('url', '==', oldImageUrl));
              const imagesSnapshot = await getDocs(imagesQuery);
              
              // Use for...of loop instead of forEach to properly await promises
              for (const imageDoc of imagesSnapshot.docs) {
                await deleteDoc(doc(db, 'images', imageDoc.id));
                console.log('Old image record deleted from images collection:', imageDoc.id);
              }
            } catch (deleteError) {
              console.error('Error deleting old image:', deleteError);
              // Don't show error to user as the update was successful
            }
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          toast.error('Menu item updated but image upload failed');
        }
      }
      
      await updateDoc(doc(db, 'menuItems', editingItem.id), {
        ...editingItemData,
        imageUrl
      });
      
      setEditingItem(null);
      clearImageSelection();
      toast.success('Menu item updated successfully');
    } catch (error) {
      console.error('Error updating menu item:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to update menu item: ${errorMessage}`);
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-4">
            <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
          </div>

          {/* Online Ordering Toggle */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-base font-semibold">Online Ordering</h3>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-sm font-medium ${
                  onlineOrderingEnabled ? 'text-green-600' : 'text-red-600'
                }`}>
                  {onlineOrderingEnabled ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  onClick={handleToggleOnlineOrdering}
                  disabled={savingSettings}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    onlineOrderingEnabled ? 'bg-green-600' : 'bg-gray-300'
                  } ${savingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      onlineOrderingEnabled ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 mb-6 sm:mb-8 bg-gray-200 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('menu')}
              className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 rounded-md transition-colors text-sm sm:text-base ${
                activeTab === 'menu'
                  ? 'bg-white text-red-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Menu className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Menu Management</span>
              <span className="sm:hidden">Menu</span>
            </button>
            <button
              onClick={() => setActiveTab('hours')}
              className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 rounded-md transition-colors text-sm sm:text-base ${
                activeTab === 'hours'
                  ? 'bg-white text-red-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Opening Hours</span>
              <span className="sm:hidden">Hours</span>
            </button>
          </div>

          {/* Menu Management Tab */}
          {activeTab === 'menu' && (
            <div className="space-y-6 sm:space-y-8">
              {/* Menu Management Section */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                {/* Categories Management */}
                <div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleCategoryDragEnd}
                  >
                    <SortableContext
                      items={categories.map((cat) => cat.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {categories.map((category) => {
                          const categoryItems = menuItems
                            .filter(item => item.categoryId === category.id)
                            .sort((a, b) => a.order - b.order);
                          
                          return (
                            <CollapsibleCategory
                              key={category.id}
                              category={category}
                              items={categoryItems}
                              onEditCategory={handleEditCategory}
                              onDeleteCategory={handleDeleteCategory}
                              onReorderItems={(items) => handleMenuItemReorder(category.id, items)}
                              onEditItem={handleEditItem}
                              onDeleteItem={handleDeleteItem}
                              onToggleAvailability={(itemId, isAvailable) => handleUpdateItem(itemId, { isAvailable })}
                              onAddItem={handleAddItemToCategory}
                            />
                          );
                        })}
                    </div>
                    </SortableContext>
                  </DndContext>

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => {
                        setShowCategoryForm(true);
                        window.scrollTo(0, 0);
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Category
                    </button>
                  </div>
                </div>
                      </div>
            </div>
          )}


          {/* Opening Hours Tab */}
          {activeTab === 'hours' && (
            <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6">
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-3 sm:mb-4 md:mb-6">Opening Hours</h2>
              
              <div className="space-y-2 sm:space-y-3">
                {Object.entries(localOpeningHours).map(([day, hours]) => (
                  <div key={day} className="flex items-center justify-between p-2 sm:p-3 border border-gray-200 rounded-lg bg-gray-50">
                    <span className="font-semibold capitalize text-sm sm:text-base min-w-[80px] sm:min-w-[100px]">{day}</span>
                    <div className="flex items-center gap-1 sm:gap-2 flex-1 justify-end">
                      {hours.closed ? (
                        <button
                          onClick={() => setLocalOpeningHours(prev => ({
                            ...prev,
                            [day]: { ...prev[day as keyof OpeningHours], closed: false }
                          }))}
                          className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded text-xs sm:text-sm hover:bg-red-200 transition-colors whitespace-nowrap"
                        >
                          Closed
                        </button>
                      ) : (
                        <>
                          <input
                            type="time"
                            value={hours.open}
                            onChange={(e) => setLocalOpeningHours(prev => ({
                              ...prev,
                              [day]: { ...prev[day as keyof OpeningHours], open: e.target.value }
                            }))}
                            className="border border-gray-300 rounded px-1.5 sm:px-2 py-1 text-xs sm:text-sm w-[90px] sm:w-24"
                          />
                          <span className="text-xs sm:text-sm text-gray-500">-</span>
                          <input
                            type="time"
                            value={hours.close}
                            onChange={(e) => setLocalOpeningHours(prev => ({
                              ...prev,
                              [day]: { ...prev[day as keyof OpeningHours], close: e.target.value }
                            }))}
                            className="border border-gray-300 rounded px-1.5 sm:px-2 py-1 text-xs sm:text-sm w-[90px] sm:w-24"
                          />
                          <button
                            onClick={() => setLocalOpeningHours(prev => ({
                              ...prev,
                              [day]: { ...prev[day as keyof OpeningHours], closed: true }
                            }))}
                            className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-gray-200 hover:bg-red-100 text-gray-600 hover:text-red-600 rounded text-sm sm:text-base transition-colors flex-shrink-0"
                            title="Mark as closed"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 sm:mt-6 flex justify-end">
                <button
                  onClick={handleSaveHours}
                  disabled={!hasHoursChanges}
                  className={`text-sm sm:text-base px-3 sm:px-4 py-2 rounded-md font-semibold flex items-center transition-all ${
                    hasHoursChanges
                      ? 'bg-red-600 hover:bg-red-700 text-white cursor-pointer shadow-md'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {hasHoursChanges ? 'Save Hours' : 'No Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-md w-full p-6 my-8"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Add Category</h3>
              <button
                onClick={() => setShowCategoryForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="form-input"
                  placeholder="Enter category name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="form-input form-textarea"
                  placeholder="Enter category description"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="category-active"
                  checked={newCategory.isActive}
                  onChange={(e) => setNewCategory({ ...newCategory, isActive: e.target.checked })}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="category-active" className={`text-sm font-medium ${newCategory.isActive ? 'text-gray-700' : 'text-red-600'}`}>
                  Category is {newCategory.isActive ? 'active' : 'inactive'} (
                  {newCategory.isActive ? 'visible to' : 'hidden from'} customers)
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCategoryForm(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                className="btn btn-primary"
              >
                Add Category
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Item Form Modal */}
      {showItemForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Add Menu Item</h3>
              <button
                onClick={() => setShowItemForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Name
                </label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  className="form-input"
                  placeholder="Enter item name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  className="form-input form-textarea"
                  placeholder="Enter item description"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (£)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newItem.price === 0 ? '' : newItem.price}
                    onChange={(e) => setNewItem({ ...newItem, price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    className="form-input"
                    placeholder="0.00"
                />
              </div>
            </div>

            {/* Extras Management */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extras
              </label>
              <div className="space-y-3">
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={newExtra.name}
                    onChange={(e) => setNewExtra({ ...newExtra, name: e.target.value })}
                    className="flex-1 min-w-0 border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Extra name (e.g., Whipped Cream, Extra Shot, Soy Milk)"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newExtra.price === 0 ? '' : newExtra.price}
                    onChange={(e) => setNewExtra({ ...newExtra, price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    className="w-24 border border-gray-300 rounded-md px-3 py-2"
                    placeholder="0.50"
                  />
                  <button
                    type="button"
                    onClick={handleAddExtraToForm}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
                  >
                    Add Extra
                  </button>
                </div>
                
                {newItem.extras.length > 0 && (
                  <div className="space-y-1">
                    {newItem.extras.map((extra) => (
                      <div key={extra.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded">
                        <span className="text-sm">{extra.name} (+£{extra.price.toFixed(2)})</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveExtraFromForm(extra.id)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAddCategoryDropdown(v => !v)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-left bg-white"
                  >
                    {newItem.categoryId
                      ? categories.find(c => c.id === newItem.categoryId)?.name || 'Select a category'
                      : 'Select a category'}
                    <span className="float-right text-gray-500">▾</span>
                  </button>
                  {showAddCategoryDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      {categories.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No categories</div>
                      ) : (
                        categories.map((category) => (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => {
                              setNewItem({ ...newItem, categoryId: category.id });
                              setShowAddCategoryDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                              newItem.categoryId === category.id ? 'bg-gray-50' : ''
                            }`}
                          >
                            {category.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {newItem.categoryId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Adding item to: {categories.find(c => c.id === newItem.categoryId)?.name}
                  </p>
                )}
              </div>

              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Image
                </label>
                
                {/* Image Preview */}
                {(imagePreview || newItem.imageUrl) && (
                  <div className="mb-4">
                    <img
                      src={imagePreview || newItem.imageUrl}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        clearImageSelection();
                        setNewItem({ ...newItem, imageUrl: '' });
                      }}
                      className="mt-2 text-sm text-red-600 hover:text-red-800"
                    >
                      Remove Image
                    </button>
                  </div>
                )}
                
                {/* File Upload */}
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg border border-gray-300 transition-colors"
                  >
                    {selectedImage ? 'Change Image' : 'Upload Image'}
                  </label>
                  
                  {/* Manual URL Input */}
                  <div className="flex-1">
                    <input
                      type="url"
                      value={newItem.imageUrl}
                      onChange={(e) => setNewItem({ ...newItem, imageUrl: e.target.value })}
                      className="form-input"
                      placeholder="Or enter image URL"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-1">
                  Upload an image file (max 5MB) or enter an image URL
                </p>
                {imageQualityNote && (
                  <p className="text-xs text-orange-600 mt-2">{imageQualityNote}</p>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="item-available"
                  checked={newItem.isAvailable}
                  onChange={(e) => setNewItem({ ...newItem, isAvailable: e.target.checked })}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="item-available" className="text-sm font-medium text-gray-700">
                  Item is available (customers can order this item)
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowItemForm(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                disabled={uploadingImage}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingImage ? 'Uploading...' : 'Add Item'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-md my-8">
            <h3 className="text-lg font-semibold mb-4">Edit Category</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingCategoryData.name}
                  onChange={(e) => setEditingCategoryData({ ...editingCategoryData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Category name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingCategoryData.description}
                  onChange={(e) => setEditingCategoryData({ ...editingCategoryData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                  placeholder="Category description"
                />
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="edit-category-active"
                  checked={editingCategoryData.isActive}
                  onChange={(e) => setEditingCategoryData({ ...editingCategoryData, isActive: e.target.checked })}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="edit-category-active" className={`text-sm font-medium ${editingCategoryData.isActive ? 'text-gray-700' : 'text-red-600'}`}>
                  Category is {editingCategoryData.isActive ? 'active' : 'inactive'} (
                  {editingCategoryData.isActive ? 'visible to' : 'hidden from'} customers)
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setEditingCategory(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCategorySubmit}
                className="px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800"
              >
                Update Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Menu Item</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingItemData.name}
                  onChange={(e) => setEditingItemData({ ...editingItemData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Item name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingItemData.description}
                  onChange={(e) => setEditingItemData({ ...editingItemData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                  placeholder="Item description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (£)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingItemData.price === 0 ? '' : editingItemData.price}
                  onChange={(e) => setEditingItemData({ ...editingItemData, price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Extras Management */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extras
              </label>
              <div className="space-y-3">
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={newExtra.name}
                    onChange={(e) => setNewExtra({ ...newExtra, name: e.target.value })}
                    className="flex-1 min-w-0 border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Extra name (e.g., Whipped Cream, Extra Shot, Soy Milk)"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newExtra.price === 0 ? '' : newExtra.price}
                    onChange={(e) => setNewExtra({ ...newExtra, price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    className="w-24 border border-gray-300 rounded-md px-3 py-2"
                    placeholder="0.50"
                  />
                  <button
                    type="button"
                    onClick={handleAddExtraToEditingForm}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
                  >
                    Add Extra
                  </button>
                </div>
                
                {editingItemData.extras.length > 0 && (
                  <div className="space-y-1">
                    {editingItemData.extras.map((extra) => (
                      <div key={extra.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded">
                        <span className="text-sm">{extra.name} (+£{extra.price.toFixed(2)})</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveExtraFromEditingForm(extra.id)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={editingItemData.categoryId}
                  onChange={(e) => setEditingItemData({ ...editingItemData, categoryId: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Image
                </label>
                
                {/* Image Preview */}
                {(imagePreview || editingItemData.imageUrl) && (
                  <div className="mb-4">
                    <img
                      src={imagePreview || editingItemData.imageUrl}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        clearImageSelection();
                        setEditingItemData({ ...editingItemData, imageUrl: '' });
                      }}
                      className="mt-2 text-sm text-red-600 hover:text-red-800"
                    >
                      Remove Image
                    </button>
                  </div>
                )}
                
                {/* File Upload */}
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="edit-image-upload"
                  />
                  <label
                    htmlFor="edit-image-upload"
                    className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg border border-gray-300 transition-colors"
                  >
                    {selectedImage ? 'Change Image' : 'Upload New Image'}
                  </label>
                  
                  {/* Manual URL Input */}
                  <div className="flex-1">
                    <input
                      type="url"
                      value={editingItemData.imageUrl}
                      onChange={(e) => setEditingItemData({ ...editingItemData, imageUrl: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Or enter image URL"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-1">
                  Upload an image file (max 5MB) or enter an image URL
                </p>
                {imageQualityNote && (
                  <p className="text-xs text-orange-600 mt-2">{imageQualityNote}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-4">
              <input
                type="checkbox"
                id="edit-item-available"
                checked={editingItemData.isAvailable}
                onChange={(e) => setEditingItemData({ ...editingItemData, isAvailable: e.target.checked })}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label htmlFor="edit-item-available" className="text-sm font-medium text-gray-700">
                Item is available (customers can order this item)
              </label>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setEditingItem(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateItemSubmit}
                disabled={uploadingImage}
                className="px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingImage ? 'Uploading...' : 'Update Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmToggleOrdering}
        title={confirmModalType === 'enable' ? 'Enable Online Ordering' : 'Disable Online Ordering'}
        message={
          confirmModalType === 'enable'
            ? '⚠️ Warning: This will delete all order history and start order numbers from #1. Make sure all previous orders are completed. Are you ready to enable online ordering?'
            : '⚠️ This will prevent customers from placing new orders. Existing orders will remain visible until you complete them. Do you want to continue?'
        }
        confirmText={confirmModalType === 'enable' ? 'Yes, Enable and Clear History' : 'Yes, Disable Online Ordering'}
        cancelText="Cancel"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmation.show}
        onClose={() => setDeleteConfirmation({ show: false, type: null, id: null, name: '' })}
        onConfirm={confirmDelete}
        title={`Cancel ${deleteConfirmation.type === 'category' ? 'Category' : 'Menu Item'}`}
        message={
          deleteConfirmation.type === 'category' 
            ? `Are you sure you want to cancel the category "${deleteConfirmation.name}"?`
            : `Are you sure you want to cancel "${deleteConfirmation.name}"? This will also remove the associated image.`
        }
        confirmText="Cancel"
        cancelText="Cancel"
      />

    </div>
  );
};

export default AdminDashboard;
